import type {
  CircleLocale,
  LocaleBcp47,
} from "@circle-react/types/CircleLocale";

const circleLocaleToBcp47: Record<CircleLocale, LocaleBcp47> = {
  en: "en-US",
  pt: "pt-BR",
  "pt-PT": "pt-PT",
  es: "es-ES",
  fr: "fr-FR",
  it: "it-IT",
  de: "de-DE",
};

export const getLocale = (locale?: CircleLocale): LocaleBcp47 => {
  if (!locale) {
    return "en-US";
  }

  return circleLocaleToBcp47[locale] || "en-US";
};

export const getCurrentCircleLocale = (): CircleLocale =>
  window?.locale ?? "en";

export const getCurrentBcp47Locale = (): LocaleBcp47 =>
  getLocale(window?.locale);

export const dynamicVariablesCorrectedTranslation = (translation: string) =>
  translation.replace(/{([\w.]+)}/g, "{{$1}}");

const pluralOrdinalRules = {
  en: new Intl.PluralRules("en-US", { type: "ordinal" }),
  pt: new Intl.PluralRules("pt-BR", { type: "ordinal" }),
  "pt-PT": new Intl.PluralRules("pt-PT", { type: "ordinal" }),
  es: new Intl.PluralRules("es-ES", { type: "ordinal" }),
  fr: new Intl.PluralRules("fr-FR", { type: "ordinal" }),
  it: new Intl.PluralRules("it-IT", { type: "ordinal" }),
  de: new Intl.PluralRules("de-DE", { type: "ordinal" }),
};

const ordinalSuffix = {
  en: new Map([
    ["one", "st"],
    ["two", "nd"],
    ["few", "rd"],
    ["other", "th"],
  ]),
  pt: new Map([["other", "°"]]),
  "pt-PT": new Map([["other", "°"]]),
  es: new Map([["other", "°"]]),
  fr: new Map([
    ["one", "er"],
    ["other", "e"],
  ]),
  it: new Map([["other", "°"]]),
  de: new Map([["other", "."]]),
};

export const getOrdinal = (input: number): string => {
  const circleLocale = getCurrentCircleLocale();
  const selectPluralOrdinalRules =
    pluralOrdinalRules[circleLocale] || pluralOrdinalRules["en"];

  const rule = selectPluralOrdinalRules.select(input);
  const selectOrdinalSuffix =
    ordinalSuffix[circleLocale] || ordinalSuffix["en"];

  return `${input}${selectOrdinalSuffix.get(rule)}`;
};

export const isCurrentLocaleCommaDecimalSeparator = () => {
  const numberWithDecimal = 1.1;
  const locale = getCurrentBcp47Locale();

  const parts = new Intl.NumberFormat(locale).formatToParts(numberWithDecimal);
  const decimalPart = parts.find(part => part.type === "decimal") || {
    type: "decimal",
    value: ".",
  };

  return decimalPart.value === ",";
};

export const formatCount = (count: number): string =>
  new Intl.NumberFormat(getCurrentBcp47Locale()).format(count);

export const formatCurrencyAmount = (
  amountInCents: number,
  currency: string,
): string =>
  new Intl.NumberFormat(getCurrentBcp47Locale(), {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amountInCents / 100);

export const formatCurrencyAmountWithCode = (
  amountInCents: number,
  currency: string,
): string =>
  `${formatCurrencyAmount(amountInCents, currency)} ${currency.toUpperCase()}`;
